(() => {
  const config = window.NixingDemoCurrency;
  if (!config || config.baseCurrency !== 'USD' || !config.messages) return;

  const day = 86400000;
  const cacheKey = 'nixing-reference-rates-v1-USD';
  const originals = new Map();
  let activeData = null;
  let status;
  let statusText;
  let started = false;
  const validData = (data) => {
    const now = Date.now();
    const last = data?.time_last_update_unix * 1000;
    const next = data?.time_next_update_unix * 1000;
    const rate = data?.rates?.[config.currency];
    return data?.result === 'success' && data.base_code === 'USD' &&
      typeof rate === 'number' && Number.isFinite(rate) && rate > 0 &&
      Number.isFinite(last) && last > 0 && last <= now + 300000 && now - last <= 7 * day &&
      Number.isFinite(next) && next > last && next - last <= 2 * day;
  };
  const showStatus = (state) => {
    let message = config.messages[state];
    if (activeData) {
      const date = new Intl.DateTimeFormat(config.locale, {
        dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC',
      }).format(new Date(activeData.time_last_update_unix * 1000));
      message = message.replace('__DATE__', `${date} UTC`);
    }
    if (statusText.nodeValue !== message) statusText.nodeValue = message;
    status.dataset.rateState = state;
  };

  const formatter = new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: config.currency === 'VND' ? 0 : 2,
    maximumFractionDigits: config.currency === 'VND' ? 0 : 2,
  });
  const usdPattern = /(?:\bUSD\s*\$?\s*|\$\s*)([\d,]+(?:\.\d{1,2})?)(?:\s*USD\b)?/g;
  const blockedTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'OPTION', 'INPUT']);

  const formatLocal = (usdValue) => {
    const converted = usdValue * activeData.rates[config.currency];
    const rounded = Math.round(converted / config.rounding) * config.rounding;
    return `≈ ${formatter.format(rounded)}`;
  };

  const convertTextNode = (node) => {
    const parent = node.parentElement;
    if (!parent || blockedTags.has(parent.tagName) || parent.closest('[data-currency-reference], script, style, textarea, [contenteditable]')) return;
    const text = node.nodeValue;
    const previous = originals.get(node);
    const source = previous && text === previous.rendered ? previous.source : text;
    if (!source || (!source.includes('$') && !source.includes('USD'))) {
      originals.delete(node);
      return;
    }
    const rendered = activeData ? source.replace(usdPattern, (match, amount) => {
      const usdValue = Number.parseFloat(amount.replaceAll(',', ''));
      return Number.isFinite(usdValue) ? formatLocal(usdValue) : match;
    }) : source;
    originals.set(node, { source, rendered });
    if (text !== rendered) node.nodeValue = rendered;
  };

  const convertTree = (root) => {
    if (root.nodeType === Node.TEXT_NODE) {
      convertTextNode(root);
      return;
    }
    if (!(root instanceof Element) && root !== document.body) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      convertTextNode(node);
      node = walker.nextNode();
    }
  };

  const start = () => {
    if (started) return;
    started = true;
    status = document.createElement('div');
    status.id = 'nixing-currency-reference';
    status.dataset.currencyReference = '';
    status.setAttribute('role', 'status');
    status.hidden = true;
    statusText = document.createTextNode('');
    const attribution = document.createElement('a');
    attribution.href = 'https://www.exchangerate-api.com';
    attribution.textContent = config.messages.attribution;
    attribution.rel = 'noreferrer';
    const sourceNote = document.createElement('div');
    sourceNote.id = 'nixing-currency-source';
    sourceNote.dataset.currencyReference = '';
    sourceNote.style.cssText = 'font-size:12px;line-height:1.5;padding:8px 16px;';
    status.append(statusText);
    sourceNote.append(status, attribution);
    (document.querySelector('footer') || document.body).append(sourceNote);
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (validData(cached)) activeData = cached;
    } catch {}
    showStatus(activeData ? (activeData.time_next_update_unix * 1000 > Date.now() ? 'updated' : 'cached') : 'loading');
    document.documentElement.dataset.demoCurrency = config.currency;
    convertTree(document.body);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') convertTextNode(mutation.target);
        for (const node of mutation.addedNodes) convertTree(node);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    const refresh = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      let delay = 3600000;
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD', {
          signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer',
        });
        if (!response.ok) throw new Error('rate_request_failed');
        const data = await response.json();
        if (!validData(data) || Date.now() - data.time_last_update_unix * 1000 > 2 * day ||
          (activeData && data.time_last_update_unix < activeData.time_last_update_unix)) throw new Error('rate_data_invalid');
        activeData = data;
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
        for (const [node] of originals) {
          if (node.isConnected) convertTextNode(node);
          else originals.delete(node);
        }
        convertTree(document.body);
        showStatus(data.time_next_update_unix * 1000 > Date.now() ? 'updated' : 'cached');
        delay = Math.max(3600000, Math.min(day, data.time_next_update_unix * 1000 - Date.now()));
      } catch {
        if (activeData && !validData(activeData)) {
          activeData = null;
          for (const [node] of originals) {
            if (node.isConnected) convertTextNode(node);
            else originals.delete(node);
          }
        }
        showStatus(activeData ? 'cached' : 'unavailable');
      } finally {
        clearTimeout(timeout);
        setTimeout(refresh, delay);
      }
    };
    if (!activeData || activeData.time_next_update_unix * 1000 <= Date.now()) {
      refresh();
    } else {
      setTimeout(refresh, Math.min(day, activeData.time_next_update_unix * 1000 - Date.now()));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
